/**
 * PublicCompare — Funcionalidade 7 (Fase 1)
 *
 * Public side-by-side candidate comparison. Route: /comparar?a={slug}&b={slug}
 * No login required. Shareable via WhatsApp.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { CandidateAvatar } from "../components/CandidateAvatar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const brl = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

const partySigla = (p) => (p || "").match(/\(([^)]+)\)/)?.[1] || p || "—";

const verdictClass = (code) =>
  code === "CLEAN"
    ? "text-[#006030] bg-[#E8F5E9]"
    : code === "PENDING"
    ? "text-[#DC2626] bg-[#FEE2E2]"
    : "text-slate-600 bg-slate-100";

const scoreColor = (score) => {
  if (score >= 7) return "text-[#006030]";
  if (score >= 4) return "text-[#F59E0B]";
  return "text-[#DC2626]";
};

const Row = ({ label, a, b, highlight }) => (
  <div className={`grid grid-cols-3 gap-2 py-3 px-4 border-b border-slate-100 ${highlight ? "bg-slate-50" : ""}`}>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 self-center">{label}</div>
    <div className="text-sm text-slate-800 font-medium text-center">{a}</div>
    <div className="text-sm text-slate-800 font-medium text-center">{b}</div>
  </div>
);

export default function PublicCompare() {
  const [params, setParams] = useSearchParams();
  const a = params.get("a");
  const b = params.get("b");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // "picker" state when slugs are missing/invalid — user types 2 names
  const [pickerA, setPickerA] = useState("");
  const [pickerB, setPickerB] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestQuery, setSuggestQuery] = useState({ target: null, q: "" });

  useEffect(() => {
    if (!a || !b) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setErr(null);
    fetch(`${BACKEND}/api/public/comparar?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "compare_failed");
        return r.json();
      })
      .then((json) => { if (!cancelled) setData(json); })
      .catch((e) => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [a, b]);

  // Very simple suggestion — just fetches /api/candidates and filters client-side.
  const loadSuggestions = useCallback(async (target, q) => {
    setSuggestQuery({ target, q });
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const r = await fetch(`${BACKEND}/api/candidates`);
      if (!r.ok) return;
      const list = await r.json();
      const filtered = list
        .filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase()))
        .slice(0, 6)
        .map((c) => ({
          slug: (c.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + (c.id || "").slice(-4).toLowerCase(),
          name: c.name,
          party: c.current_party,
        }));
      setSuggestions(filtered);
    } catch { /* silent */ }
  }, []);

  const applySelection = (target, slug) => {
    if (target === "a") setPickerA(slug);
    else setPickerB(slug);
    setSuggestions([]);
    setSuggestQuery({ target: null, q: "" });
  };

  const runCompare = () => {
    if (!pickerA || !pickerB) {
      toast.error("Escolha dois candidatos para comparar");
      return;
    }
    if (pickerA === pickerB) {
      toast.error("Escolha candidatos diferentes");
      return;
    }
    setParams({ a: pickerA, b: pickerB });
  };

  const shareWhatsApp = () => {
    if (!data) return;
    const text =
      `Comparei ${data.a.name} e ${data.b.name} no Tô de Olho.\n` +
      `${data.a.name} (${partySigla(data.a.current_party)}): nota ${data.a.integrity_score}/10\n` +
      `${data.b.name} (${partySigla(data.b.current_party)}): nota ${data.b.integrity_score}/10\n` +
      `Confira: ${BACKEND}/comparar?a=${data.a.slug}&b=${data.b.slug}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const title = data
    ? `${data.a.name} × ${data.b.name} | Tô de Olho`
    : "Comparar candidatos | Tô de Olho";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="public-compare-page">
      <Helmet>
        <title>{title}</title>
        <meta property="og:title" content={title} />
      </Helmet>

      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-black text-slate-800 mb-1">Comparar dois candidatos</h1>
        <p className="text-sm text-slate-600">
          Escolha dois políticos e veja lado a lado nota, veredicto, partido e histórico.
        </p>
      </header>

      {/* Picker */}
      {(!a || !b) && (
        <section className="bg-white rounded-2xl shadow-md p-6 mb-6" data-testid="compare-picker">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {["a", "b"].map((target) => (
              <div key={target} className="relative">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">
                  {target === "a" ? "Candidato A" : "Candidato B"}
                </label>
                <input
                  type="text"
                  placeholder="Digite o nome…"
                  onChange={(e) => loadSuggestions(target, e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006030]/30 min-h-[44px]"
                  data-testid={`compare-input-${target}`}
                />
                {suggestQuery.target === target && suggestions.length > 0 && (
                  <ul className="absolute z-10 bg-white border border-slate-200 rounded-lg mt-1 w-full max-h-56 overflow-y-auto shadow-lg">
                    {suggestions.map((s) => (
                      <li key={s.slug}>
                        <button
                          type="button"
                          onClick={() => applySelection(target, s.slug)}
                          className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm text-slate-700 flex items-center gap-2"
                          data-testid={`compare-suggestion-${target}-${s.slug}`}
                        >
                          <span className="font-medium">{s.name}</span>
                          <span className="text-xs text-slate-500">· {partySigla(s.party)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {(target === "a" ? pickerA : pickerB) && (
                  <p className="text-xs text-[#006030] mt-1">Selecionado: {(target === "a" ? pickerA : pickerB)}</p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={runCompare}
            className="w-full bg-[#006030] hover:bg-[#004a24] text-white font-bold py-3 rounded-lg min-h-[44px] transition-colors"
            data-testid="compare-run-btn"
          >
            Comparar candidatos
          </button>
        </section>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-10 text-slate-600" data-testid="compare-loading">
          Carregando comparação…
        </div>
      )}

      {/* Error */}
      {err && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4" data-testid="compare-error">
          {err.includes("não encontrado") ? "Ops! Um dos candidatos não foi encontrado. Tente novamente." : "Não foi possível comparar agora. Tente em alguns instantes."}
        </div>
      )}

      {/* Result */}
      {data && !loading && (
        <>
          <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" data-testid="compare-result">
            <div className="grid grid-cols-2">
              {["a", "b"].map((k) => (
                <div key={k} className="p-6 text-center border-slate-100 border-b" data-testid={`compare-column-${k}`}>
                  <div className="flex justify-center mb-3">
                    <CandidateAvatar slug={data[k].slug} name={data[k].name} party={data[k].current_party} size={96} />
                  </div>
                  <h2 className="text-lg font-bold text-slate-800 leading-tight">{data[k].name}</h2>
                  <p className="text-xs text-slate-500">{partySigla(data[k].current_party)} · {data[k].position}</p>
                </div>
              ))}
            </div>

            <div className="divide-y divide-slate-100">
              <Row
                label="Nota"
                a={<span className={`text-2xl font-black ${scoreColor(data.a.integrity_score)}`} data-testid="compare-score-a">{data.a.integrity_score}/10</span>}
                b={<span className={`text-2xl font-black ${scoreColor(data.b.integrity_score)}`} data-testid="compare-score-b">{data.b.integrity_score}/10</span>}
              />
              <Row
                label="Veredicto"
                a={<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${verdictClass(data.a.verdict?.code)}`}>{data.a.verdict?.titulo}</span>}
                b={<span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${verdictClass(data.b.verdict?.code)}`}>{data.b.verdict?.titulo}</span>}
                highlight
              />
              <Row
                label="Estado"
                a={data.a.state || "—"}
                b={data.b.state || "—"}
              />
              <Row
                label="Bens declarados"
                a={brl(data.a.bens_total)}
                b={brl(data.b.bens_total)}
                highlight
              />
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <button
              type="button"
              onClick={shareWhatsApp}
              className="flex-1 bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-3 rounded-lg min-h-[44px] transition-colors"
              data-testid="compare-share-whatsapp"
            >
              Compartilhar no WhatsApp
            </button>
            <Link
              to={`/candidato/${data.a.slug}`}
              className="flex-1 text-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3 rounded-lg min-h-[44px] transition-colors flex items-center justify-center"
              data-testid="compare-detail-a"
            >
              Ver detalhes de {data.a.name}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => { setParams({}); setPickerA(""); setPickerB(""); }}
            className="text-sm text-slate-600 underline hover:text-slate-800"
            data-testid="compare-new-btn"
          >
            Comparar outros candidatos
          </button>
        </>
      )}
    </div>
  );
}
